import React, { useContext, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, Card, CardContent, CardHeader, CircularProgress, Grid } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import FormInput from '../../ReactHookForm/FormInput/FormInput';
import InfoTooltip from '../../InfoTooltip';
import { AppContext } from '../../../AppContext';
import { getValuationFitConfiguration, saveValuationFitConfiguration } from '../../../services';

const FIT_URL_TOOLTIP =
  'This value is typically provided by Moreland, or can be copied from another client configuration belonging to the same account.';

const styles = () => ({
  form: {
    width: '100%',
  },
  settingsCard: {
    height: '100%',
  },
  settingsCardHeader: {
    padding: '14px 18px',
    backgroundColor: '#f0f0f0',
    borderBottom: '1px solid #e0e0e0',
  },
  settingsCardHeaderTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: 'var(--jmi-blue)',
    letterSpacing: '0.01em',
  },
  settingsCardContent: {
    padding: '16px',
    '&:last-child': {
      paddingBottom: 16,
    },
  },
  urlFieldLabel: {
    margin: 0,
    marginBottom: 2,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.2,
    color: '#777',
    display: 'flex',
    alignItems: 'center',
  },
  urlInfoIconWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 4,
    cursor: 'pointer',
    lineHeight: 0,
  },
  urlInfoIcon: {
    fontSize: 14,
    color: 'rgba(0, 0, 0, 0.54)',
  },
});

const SettingsSectionCard = withStyles(styles)(({ classes, title, children }) => (
  <Card className={cn(classes.settingsCard, 'border-left-green')} elevation={2}>
    <CardHeader
      className={classes.settingsCardHeader}
      title={title}
      titleTypographyProps={{ className: classes.settingsCardHeaderTitle }}
    />
    <CardContent className={classes.settingsCardContent}>{children}</CardContent>
  </Card>
));

const UrlFieldWithInfo = ({ name, classes }) => (
  <>
    <p className={classes.urlFieldLabel}>
      URL
      <InfoTooltip title={FIT_URL_TOOLTIP} arrow placement="top">
        <span className={classes.urlInfoIconWrap} role="img" aria-label="About URL">
          <InfoOutlinedIcon className={classes.urlInfoIcon} />
        </span>
      </InfoTooltip>
    </p>
    <FormInput type="text" name={name} label="" />
  </>
);

const emptyCredentialShape = clientId => ({
  e2VCredential: {
    username: '',
    password: '',
    clientId: Number(clientId),
  },
  isoCredential: {
    username: '',
    password: '',
    url: '',
    groupId: '',
    clientId: Number(clientId),
  },
  coreLogicCredential: {
    username: '',
    password: '',
    url: '',
    clientId: Number(clientId),
  },
});

const formValidationSchema = Yup.object({
  e2VCredential: Yup.object(),
  isoCredential: Yup.object(),
  coreLogicCredential: Yup.object(),
});

const toFormValues = (api, clientId) => {
  const base = emptyCredentialShape(clientId);
  if (!api || typeof api !== 'object') {
    return base;
  }
  const pick = (src, keys) => {
    if (!src) return {};
    const out = {};
    keys.forEach(k => {
      const val = src[k];
      out[k] = val === null || val === undefined ? '' : val;
    });
    return out;
  };
  return {
    e2VCredential: {
      ...base.e2VCredential,
      ...pick(api.e2VCredential, ['username', 'password']),
      clientId: Number(clientId),
    },
    isoCredential: {
      ...base.isoCredential,
      ...pick(api.isoCredential, ['username', 'password', 'url', 'groupId']),
      clientId: Number(clientId),
    },
    coreLogicCredential: {
      ...base.coreLogicCredential,
      ...pick(api.coreLogicCredential, ['username', 'password', 'url']),
      clientId: Number(clientId),
    },
  };
};

/** Deep-clone last GET response and overlay editable fields so PUT matches GET shape. */
const buildPutPayload = (original, formValues, clientId) => {
  const cid = Number(clientId);
  const clone =
    original && typeof original === 'object' ? JSON.parse(JSON.stringify(original)) : {};

  const patchBlock = (key, updates) => {
    const prev = clone[key] && typeof clone[key] === 'object' ? clone[key] : {};
    clone[key] = { ...prev, ...updates, clientId: cid };
  };

  patchBlock('e2VCredential', {
    username: formValues.e2VCredential?.username ?? '',
    password: formValues.e2VCredential?.password ?? '',
  });
  const rawGroupId = formValues.isoCredential?.groupId;
  let isoGroupId;
  if (rawGroupId === '' || rawGroupId === null || rawGroupId === undefined) {
    isoGroupId = 0;
  } else {
    const n = Number(rawGroupId);
    isoGroupId = Number.isFinite(n) ? n : (clone.isoCredential?.groupId ?? 0);
  }

  patchBlock('isoCredential', {
    username: formValues.isoCredential?.username ?? '',
    password: formValues.isoCredential?.password ?? '',
    url: formValues.isoCredential?.url ?? '',
    groupId: isoGroupId,
  });
  patchBlock('coreLogicCredential', {
    username: formValues.coreLogicCredential?.username ?? '',
    password: formValues.coreLogicCredential?.password ?? '',
    url: formValues.coreLogicCredential?.url ?? '',
  });

  return clone;
};

export const FitCredentialsTab = withStyles(styles)(({ classes, clientId }) => {
  const { dispatch: appDispatch } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const originalRef = useRef(null);

  const formMethods = useForm({
    resolver: yupResolver(formValidationSchema),
    reValidateMode: 'onSubmit',
    defaultValues: emptyCredentialShape(clientId),
  });

  const load = async () => {
    setLoadError(null);
    try {
      const data = await getValuationFitConfiguration(clientId);
      originalRef.current = data;
      formMethods.reset(toFormValues(data, clientId));
    } catch (e) {
      originalRef.current = null;
      formMethods.reset(emptyCredentialShape(clientId));
      setLoadError(e?.message || 'Failed to load FIT credentials');
    }
  };

  useEffect(() => {
    load();
  }, [clientId]);

  const onSubmit = async formValues => {
    setIsLoading(true);
    setSaveError(null);
    try {
      const payload = buildPutPayload(originalRef.current, formValues, clientId);
      await saveValuationFitConfiguration(clientId, payload);
      setSaveError(null);
      await load();
      appDispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: 'FIT credentials saved successfully.',
          variant: 'success',
        },
      });
    } catch (e) {
      console.error('Error saving FIT credentials:', e);
      setSaveError(e?.message || 'Save failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormProvider {...formMethods} schema={formValidationSchema}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)} className={classes.form}>
        <Grid container spacing={3}>
          {loadError && (
            <Grid item xs={12}>
              <span style={{ color: '#c62828' }}>{loadError}</span>
            </Grid>
          )}
          {saveError && (
            <Grid item xs={12}>
              <span style={{ color: '#c62828' }}>{saveError}</span>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <SettingsSectionCard title="E2Value Credentials">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormInput type="text" name="e2VCredential.username" label="Username" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput type="password" name="e2VCredential.password" label="Password" passwordPeekable />
                </Grid>
              </Grid>
            </SettingsSectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SettingsSectionCard title="ISO 360 Credentials">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormInput type="text" name="isoCredential.username" label="Username" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput type="password" name="isoCredential.password" label="Password" passwordPeekable />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UrlFieldWithInfo name="isoCredential.url" classes={classes} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput type="number" name="isoCredential.groupId" label="Group ID" />
                </Grid>
              </Grid>
            </SettingsSectionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <SettingsSectionCard title="CoreLogic Credentials">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormInput type="text" name="coreLogicCredential.username" label="Username" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormInput
                    type="password"
                    name="coreLogicCredential.password"
                    label="Password"
                    passwordPeekable
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <UrlFieldWithInfo name="coreLogicCredential.url" classes={classes} />
                </Grid>
              </Grid>
            </SettingsSectionCard>
          </Grid>

          <Grid item xs={12}>
            <Button
              style={{ backgroundColor: '#315382', color: '#fff' }}
              variant="contained"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <CircularProgress style={{ fill: '#fff', color: '#fff', marginRight: 10 }} size={25} />
                  Saving
                </>
              ) : (
                'Save FIT credentials'
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
});

FitCredentialsTab.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default FitCredentialsTab;
